import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { ApiError, parseErrorResponse } from "./errorHandling";
import { secureApiRequest } from "./csrfClient";

/**
 * Redirect a locked-out (subscription-inactive) user to the billing page so they can
 * re-subscribe. Guards against redirect loops/churn: never redirects when already on
 * the billing page, and fires the navigation at most once per page load even when many
 * concurrent requests return 402 at the same time.
 */
let redirectingToBilling = false;
function redirectToBilling() {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/billing") return;
  if (redirectingToBilling) return;
  redirectingToBilling = true;
  window.location.href = "/billing";
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = await res.text();
    const appError = parseErrorResponse(res, text);
    throw new ApiError(appError, res.status);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<any> {
  try {
    const res = await secureApiRequest(url, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
    });

    await throwIfResNotOk(res);
    
    // For DELETE requests that return 204, don't try to parse JSON
    if (res.status === 204) {
      return;
    }
    
    // Safely parse JSON response — handle cases where server returns non-JSON
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return await res.json();
    }
    
    // If Content-Type is not JSON, try parsing anyway but handle gracefully
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      // For successful responses (2xx), return a default success object
      // rather than throwing — many mutations don't need the response body
      if (res.ok) {
        return { success: true };
      }
      // Server returned non-JSON error response — wrap in a structured error
      throw new ApiError({
        code: 'INVALID_RESPONSE',
        message: "Server returned an unexpected response",
        resolution: "Please try again. If the problem persists, contact support"
      }, res.status);
    }
  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ApiError({
        code: 'NETWORK_ERROR',
        message: "Unable to connect to the server",
        resolution: "Please check your internet connection and try again"
      }, 0);
    }
    
    // Re-throw ApiErrors as-is, but check for session timeout first
    if (error instanceof ApiError) {
      // Handle session timeout by redirecting to login
      if (error.appError.code === 'SESSION_TIMEOUT') {
        // Clear any cached authentication state
        queryClient.setQueryData(['/api/auth/status'], null);
        queryClient.setQueryData(['/api/users/current'], null);
        // Redirect to login page
        window.location.href = '/auth';
        return; // Don't throw the error since we're redirecting
      }
      // Subscription inactive: send the user to billing to re-subscribe (soft lock).
      if (error.appError.code === 'SUBSCRIPTION_INACTIVE') {
        redirectToBilling();
        throw error;
      }
      throw error;
    }
    
    // Handle unexpected errors - preserve original message for debugging
    throw new ApiError({
      code: 'INTERNAL_ERROR',
      message: error instanceof Error ? error.message : "An unexpected error occurred",
      resolution: "Please try again. If the problem persists, contact technical support"
    }, 500);
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    try {
      const res = await secureApiRequest(queryKey.join("/") as string, {
        method: 'GET',
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (error) {
      // Handle network errors for queries
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError({
          code: 'NETWORK_ERROR',
          message: "Unable to connect to the server",
          resolution: "Please check your internet connection and try again"
        }, 0);
      }
      
      // Re-throw ApiErrors as-is, but check for session timeout first
      if (error instanceof ApiError) {
        // Handle session timeout by redirecting to login
        if (error.appError.code === 'SESSION_TIMEOUT') {
          // Clear any cached authentication state
          queryClient.setQueryData(['/api/auth/status'], null);
          queryClient.setQueryData(['/api/users/current'], null);
          // Redirect to login page
          window.location.href = '/auth';
          return null; // Return null since we're redirecting
        }
        // Subscription inactive: send the user to billing to re-subscribe (soft lock).
        if (error.appError.code === 'SUBSCRIPTION_INACTIVE') {
          redirectToBilling();
          throw error;
        }
        throw error;
      }
      
      // Handle unexpected errors
      throw new ApiError({
        code: 'INTERNAL_ERROR',
        message: "Failed to load data",
        resolution: "Please refresh the page and try again"
      }, 500);
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
